import React from 'react';
import {
  Container,
  IconWrapper,
  Title,
  Figcaption,
  Figure,
  Icon,
  Image,
  TextWrapper,
  Credit
} from './StyledSingleImage';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { compose } from 'ramda';
import { Camera } from '_utils/icons';
import StyledContentRichText from '_components/Corporate/ContentRichText/StyledContentRichText';
import { convertSrcJss, createSrcSet } from '_utils/media';
import { stripHTML } from '_utils/helpers/html';
import { imagePlaceholderSrc } from '_utils/styles';
import PropTypes from 'prop-types';

const ContainerNotice = compose(withEditModeNotice)(Container);

const SingleImage = (props) => {
  let noticeEnabled = false;
  let editorNotice = null;

  const { rendering, editMode } = props;

  const dataSource = rendering?.fields?.data?.datasource;

  if (!dataSource) {
    editorNotice = 'Single Image component has no data source and will not be displayed';
    noticeEnabled = true;

    if (!editMode) {
      return null;
    }
  }

  const image = dataSource?.image?.data || null;
  const imageSrc = image?.value?.src || null;

  if (!editMode && !imageSrc) {
    return null;
  }

  const imageCaption = dataSource?.caption?.data || null;
  const imageCredit = dataSource?.credit?.data || null;
  const imageTitle = dataSource?.title?.data || null;
  const imageClass = image?.value?.class || null;

  let showEmdash = null;

  const imageCaptionPlainText = stripHTML(imageCaption?.value);

  const showIcon = Boolean(imageCaptionPlainText || imageCredit?.value || imageTitle?.value);

  if (imageTitle?.value && (imageCaption?.value || imageCredit?.value)) {
    showEmdash = ' — ';
  }

  if (imageClass === 'scEmptyImage') {
    editorNotice = 'Single Image component will not be displayed without an image';
    noticeEnabled = true;
  }

  if (editMode) {
    return (
      <ContainerNotice noticeEnabled={noticeEnabled} noticeMessage={editorNotice}>
        <Image data-empty={imageClass === 'scEmptyImage'} field={image} />
        <p>
          <Text field={imageTitle} />
        </p>
        <p>
          <StyledContentRichText field={imageCaption} />
        </p>
        <p>
          <Text field={imageCredit} />
        </p>
      </ContainerNotice>
    );
  }

  return (
    <Container>
      <Figure>
        <Image
          className="lazyload"
          field={{
            value: {
              src: imagePlaceholderSrc,
              alt: image?.value?.alt,
              'data-src': convertSrcJss(imageSrc) + '?mw=640',
              'data-srcset': createSrcSet(imageSrc, [640, 1200, 1600])
            }
          }}
        />
        <Figcaption>
          {showIcon && (
            <IconWrapper>
              <Icon>
                <Camera />
              </Icon>
            </IconWrapper>
          )}
          <TextWrapper>
            {imageTitle?.value && <Title>{imageTitle.value}</Title>}
            {showEmdash}
            {imageCaption?.value && <StyledContentRichText tag="span" field={imageCaption} />}
            {imageCredit?.value && <Credit>{imageCredit.value}</Credit>}
          </TextWrapper>
        </Figcaption>
      </Figure>
    </Container>
  );
};

SingleImage.propTypes = {
  rendering: PropTypes.shape({
    fields: PropTypes.shape({
      data: PropTypes.shape({
        datasource: PropTypes.shape({
          image: PropTypes.shape({
            data: PropTypes.object,
            value: PropTypes.shape({
              src: PropTypes.string,
              class: PropTypes.string,
              alt: PropTypes.string,
            }),
          }),
          caption: PropTypes.shape({
            data: PropTypes.object,
            value: PropTypes.string,
          }),
          credit: PropTypes.shape({
            data: PropTypes.object,
            value: PropTypes.string,
          }),
          title: PropTypes.shape({
            data: PropTypes.object,
            value: PropTypes.string,
          }),
        }),
      }),
    }),
  }),
  editMode: PropTypes.bool,
};

export default compose(withEditMode)(SingleImage);
